/**
 * Get user initials from name or email
 * @param name - User's full name (optional)
 * @param email - User's email address (optional)
 * @returns Uppercase initials string
 */
export function getInitials(name?: string, email?: string): string {
  if (name) {
    const nameParts = name.split(" ").filter(Boolean);
    // If there are multiple parts, use the first and last initial
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts.at(-1)?.[0]}`.toUpperCase();
    }
    // If there is only one part, use the first two letters
    if (nameParts.length > 0 && nameParts[0].length > 0) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
  }
  // If there is no name, use the first letter(s) of the email
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  // Fallback to Arcade initials
  return "AR";
}

/**
 * Format a date string to a short date string
 * @param dateString - The date string to format
 * @returns A short date string
 */
export const formatShortDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

/**
 * Recursively remove empty strings and empty objects from an object
 * Useful for cleaning form data before submission
 * @param obj - The object to clean
 * @returns A new object with empty strings and empty objects removed
 */
export function removeEmptyValues<T extends Record<string, unknown>>(
  obj: T
): T {
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const result = {} as T;

  for (const key in obj) {
    if (!Object.hasOwn(obj, key)) {
      continue;
    }

    const value = obj[key];

    // Skip empty strings
    if (value === "") {
      continue;
    }

    // Recursively handle nested objects
    if (value && typeof value === "object" && !Array.isArray(value)) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const cleaned = removeEmptyValues(value as Record<string, unknown>);
      // Only add the nested object if it has properties (not empty)
      if (Object.keys(cleaned).length > 0) {
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        result[key] = cleaned as T[Extract<keyof T, string>];
      }
    } else {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      result[key] = value as T[Extract<keyof T, string>];
    }
  }

  return result;
}
